import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { editSession } from '../../redux/state/session';
import classNames from 'classnames';
import { checkUsername } from '../../helper/common';
import { error, warning } from '../../utils/console';
import { useCacheService } from '../../hook/useCacheService';
import { useDataService } from '../../hook/useDataService';
import { useSocket } from '../../hook/useSocket';
import { editStatus } from '../../redux/state/status';
import axios from 'axios';
import forge from 'node-forge';
import { useTranslation } from 'react-i18next';
import { ChatInterface, SendIntentEventInterface, SendMessageEventInterface } from '../../utils/interfaces';
import { getOS } from '../../utils/navigator';
import StagingConfig from '../../config/staging.config.json';
import ProductionConfig from '../../config/production.config.json';
import { getEncrypt64 } from '../../utils/encryptionModals';
import {
    USED_LOGIN_HYPERLINK_MESSAGE_IDS_KEY,
    appendUsedLoginHyperlinkMessageId,
} from '../../utils/usedLoginHyperlink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUnlockKeyhole, faXmark, faMobileScreen, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import format from 'date-fns/format';
import { addChat } from '../../redux/state/chat';

type LoginMode = 'username' | 'mobile';

interface UiLoginModalConfig {
    botId?: number;
    title?: string | null;
    usernamePlaceholder?: string | null;
    passwordPlaceholder?: string | null;
    phoneNumberPlaceholder?: string | null;
    forgetPasswordText?: string | null;
    rememberMeText?: string | null;
    loginButtonText?: string | null;
    requiredErrorMessage?: string | null;
    invalidUsernameErrorMessage?: string | null;
    invalidPasswordErrorMessage?: string | null;
    invalidPhoneNumberErrorMessage?: string | null;
    buttonColor?: string | null;
    backgroundColor?: string | null;
    activeColor?: string | null;
    fontPrimaryColor?: string | null;
    fontSecondaryColor?: string | null;
    fontActiveColor?: string | null;
    errorColor?: string | null;
    tabType?: string | null;
    enablePhoneLogin?: boolean;
}

interface Props {
    height: number;
}

const INACTIVE_SUBMIT_BUTTON_COLOR = '#eff0f1c1';

const isLightHexColor = (hex: string) => {
    const normalized = hex.replace('#', '').slice(0, 6);
    if (normalized.length !== 6) {
        return true;
    }
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    // Relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
};

const lightenHex = (hex: string, amount = 0.55) => {
    const normalized = hex.replace('#', '').slice(0, 6);
    if (normalized.length !== 6) {
        return INACTIVE_SUBMIT_BUTTON_COLOR;
    }
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    const mix = (channel: number) =>
        Math.round(channel + (255 - channel) * amount)
            .toString(16)
            .padStart(2, '0');
    return `#${mix(r)}${mix(g)}${mix(b)}`;
};
var config: any;
var HashScriptLink = '';

const LoginModal = (props: Props) => {
    const socket = useSocket();
    
    const { setCacheByField } = useCacheService();
    const { t } = useTranslation();

    const cacheService = useCacheService();

    const dataService = useDataService();

    const [loginMode, setLoginMode] = useState<LoginMode>('username');

    const [loginUsername, setLoginUsername] = useState('');

    const [loginMobile, setLoginMobile] = useState('');

    const [loginPassword, setLoginPassword] = useState('');

    const [rememberMe, setRememberMe] = useState(false);

    const [showPassword, setShowPassword] = useState(false);

    const handleUsernameChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setLoginUsername(event.target.value);
    };

    const handleMobileChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setLoginMobile(event.target.value.replace(/[^\d]/g, ''));
    };

    const handlePasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setLoginPassword(event.target.value);
    };

    const handleRememberMeChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRememberMe(event.target.checked);
    };

    const dispatch = useDispatch();

    const closeLoginModal = () => {
        dispatch(
            editStatus({
                name: 'isLoginModal',
                value: false,
            })
        );
    };

    const openLoadingModal = () => {
        dispatch(
            editStatus({
                name: 'isLoadingModal',
                value: true,
            })
        );
    };

    const closeLoadingModal = () => {
        dispatch(
            editStatus({
                name: 'isLoadingModal',
                value: false,
            })
        );
    };

    const openSuccessLoginModal = () => {
        dispatch(
            editStatus({
                name: 'isSuccessLoginModal',
                value: true,
            })
        );
    };

    const openFailLoginModal = () => {
        dispatch(
            editStatus({
                name: 'isFailLoginModal',
                value: true,
            })
        );
    };

    const isChatAlive = useSelector(
        (state: { status: { isChatAlive: Boolean } }) => state.status.isChatAlive
    );

    const sessionId = useSelector(
        (state: { session: { sessionId: string } }) => state.session.sessionId
    );

    const pendingLoginHyperlinkMessageId = useSelector(
        (state: { status: { pendingLoginHyperlinkMessageId?: string } }) =>
            state.status.pendingLoginHyperlinkMessageId
    );

    const usedLoginHyperlinkMessageIds = useSelector(
        (state: { session: { usedLoginHyperlinkMessageIds?: string[] | string } }) =>
            state.session.usedLoginHyperlinkMessageIds
    );

    const chatId = useSelector(
        (state: { session: { chatId: string } }) => state.session.chatId
    );

    const acctId = useSelector(
        (state: { session: { acctId: string } }) => state.session.acctId
    );

    const botId = useSelector(
        (state: { session: { botId: string } }) => state.session.botId
    );

    const brandId = useSelector(
        (state: { session: { brandId: string } }) => state.session.brandId
    );

    const dbProfile = useSelector(
        (state: { session: { dbProfile: string } }) => state.session.dbProfile
    );

    const username = useSelector(
        (state: { session: { username: string } }) => state.session.username
    );

    const intentId = useSelector(
        (state: { status: { intentId: string } }) => state.status.intentId
    );

    const botButtonColor = useSelector(
        (state: { settings: { botButtonColor: string } }) =>
            state.settings.botButtonColor
    );

    const loginUiConfig = useSelector(
        (state: { settings: { Login?: UiLoginModalConfig } }) =>
            state.settings.Login
    );

    // UiLoginModal config — defined for later use; only tabType is applied in UI for now
    const loginConfigBotId = loginUiConfig?.botId;
    const loginConfigTitle = loginUiConfig?.title ?? null;
    const loginConfigUsernamePlaceholder = loginUiConfig?.usernamePlaceholder ?? null;
    const loginConfigPasswordPlaceholder = loginUiConfig?.passwordPlaceholder ?? null;
    const loginConfigPhoneNumberPlaceholder = loginUiConfig?.phoneNumberPlaceholder ?? null;
    const loginConfigForgetPasswordText = loginUiConfig?.forgetPasswordText ?? null;
    const loginConfigRememberMeText = loginUiConfig?.rememberMeText ?? null;
    const loginConfigLoginButtonText = loginUiConfig?.loginButtonText ?? null;
    const loginConfigRequiredErrorMessage = loginUiConfig?.requiredErrorMessage ?? null;
    const loginConfigInvalidUsernameErrorMessage = loginUiConfig?.invalidUsernameErrorMessage ?? null;
    const loginConfigInvalidPasswordErrorMessage = loginUiConfig?.invalidPasswordErrorMessage ?? null;
    const loginConfigInvalidPhoneNumberErrorMessage = loginUiConfig?.invalidPhoneNumberErrorMessage ?? null;
    const loginConfigButtonColor = loginUiConfig?.buttonColor ?? null;
    const loginConfigBackgroundColor = loginUiConfig?.backgroundColor ?? null;
    const loginConfigActiveColor = loginUiConfig?.activeColor ?? null;
    const loginConfigFontPrimaryColor = loginUiConfig?.fontPrimaryColor ?? null;
    const loginConfigFontSecondaryColor = loginUiConfig?.fontSecondaryColor ?? null;
    const loginConfigFontActiveColor = loginUiConfig?.fontActiveColor ?? null;
    const loginConfigErrorColor = loginUiConfig?.errorColor ?? null;
    const loginConfigTabType = loginUiConfig?.tabType ?? null;
    const loginConfigEnablePhoneLogin = loginUiConfig?.enablePhoneLogin ?? false;

    const usernamePlaceholder = loginConfigUsernamePlaceholder?.trim()
        ? loginConfigUsernamePlaceholder
        : t('loginUsername');
    const passwordPlaceholder = loginConfigPasswordPlaceholder?.trim()
        ? loginConfigPasswordPlaceholder
        : t('loginPassword');
    const mobilePlaceholder = loginConfigPhoneNumberPlaceholder?.trim()
        ? loginConfigPhoneNumberPlaceholder
        : t('loginMobilePlaceholder');
    const forgetPasswordText = loginConfigForgetPasswordText?.trim()
        ? loginConfigForgetPasswordText
        : t('forgotPassword');
    const rememberMeText = loginConfigRememberMeText?.trim()
        ? loginConfigRememberMeText
        : t('rememberMe');
    const loginButtonText = loginConfigLoginButtonText?.trim()
        ? loginConfigLoginButtonText
        : t('login');

    const botLanguage = useSelector(
        (state: { settings: { botLanguage: string } }) =>
            state.settings.botLanguage
    );

    const loginForgotPasswordLinkColor = useSelector(
        (state: { settings: { loginForgotPasswordLinkColor: string } }) =>
            state.settings.loginForgotPasswordLinkColor
    );

    const processor = useSelector(
        (state: { settings: { processor: number } }) =>
            state.settings.processor
    );

    const brandCode = useSelector(
        (state: { settings: { brandCode: string } }) => state.settings.brandCode
    )

    const marketCode = useSelector(
        (state: { settings: { marketCode: string } }) => state.settings.marketCode
    )

    const customValues = useSelector(
        (state: { settings: { customValues: string } }) =>
            state.settings.customValues
    );

    const vip = useSelector(
        (state: { session: { isVip: any } }) => state.session.isVip
    );

    const botName = useSelector(
        (state: { settings: {botName: string}}) =>
            state.settings.botName
    );

    const apiPath = useSelector(
        (state: { session: { apiPath: string } }) => state.session.apiPath
    );

    const pluginPath = useSelector(
        (state: { session: { pluginPath: string } }) => state.session.pluginPath
    );

    let parsedCustomValues = customValues ? JSON.parse(customValues) : {};
    const showForgotPassword = parsedCustomValues.hasOwnProperty('forgot_password_link') ? parsedCustomValues.forgot_password_link : "";
    const isVipBot = String(parsedCustomValues?.is_vip_bot ?? 'false').toLowerCase() === 'true';
    const mobileDialCode = t('mobileDialCode');

    const rememberedUsernameKey = `rememberedLoginUsername_${acctId}_${botId}`;
    const rememberedMobileKey = `rememberedLoginMobile_${acctId}_${botId}`;
    const rememberedLoginModeKey = `rememberedLoginMode_${acctId}_${botId}`;

    const loginIdentifier = loginMode === 'mobile' ? loginMobile : loginUsername;
    const accentColor = botButtonColor ? botButtonColor.slice(0, 7) : '#0EA5E9';
    const buttonColor = loginConfigButtonColor?.trim()
        ? loginConfigButtonColor.slice(0, 7)
        : accentColor;
    const activeColor = loginConfigActiveColor?.trim()
        ? loginConfigActiveColor.slice(0, 7)
        : accentColor;
    const modalBackgroundColor = loginConfigBackgroundColor?.trim()
        ? loginConfigBackgroundColor.slice(0, 7)
        : '#ffffff';
    const fontSecondaryColor = loginConfigFontSecondaryColor?.trim()
        ? loginConfigFontSecondaryColor.slice(0, 7)
        : null;
    const fontPrimaryColor = loginConfigFontPrimaryColor?.trim()
        ? loginConfigFontPrimaryColor.slice(0, 7)
        : '#000000';
    const isButtonTabType = loginConfigTabType === 'button';
    const isLightModalBackground = isLightHexColor(modalBackgroundColor);
    const inactiveSubmitButtonColor = isButtonTabType
        ? lightenHex(buttonColor, 0.55)
        : INACTIVE_SUBMIT_BUTTON_COLOR;
    const buttonTabTrackColor = INACTIVE_SUBMIT_BUTTON_COLOR;
    const selectedButtonTabColor = isLightModalBackground ? '#ffffff' : '#4B5563';
    const inputBackgroundColor = !isButtonTabType
        ? modalBackgroundColor
        : isLightModalBackground
            ? '#ffffff'
            : '#2F2F2F';
    const inputTextColor = !isButtonTabType
        ? fontPrimaryColor
        : isLightModalBackground
            ? '#1F2937'
            : '#F3F4F6';
    const inactiveTabColor = fontSecondaryColor || '#9CA3AF';
    const inactiveSubmitTextColor = fontSecondaryColor || (isButtonTabType ? '#ffffff' : '#cbd5e0');
    const activeSubmitTextColor = loginConfigFontActiveColor?.trim()
        ? loginConfigFontActiveColor.slice(0, 7)
        : '#ffffff';

    const isUsernameValid = /^[A-Za-z0-9]{5,16}$/.test(loginUsername.trim());
    const isMobileValid = (() => {
        const mobile = loginMobile.trim();
        if (mobileDialCode === '86') {
            return /^\d{11}$/.test(mobile);
        }
        if (mobileDialCode === '66') {
            return /^[1-9]\d{8}$/.test(mobile);
        }
        if (mobileDialCode === '84') {
            return /^[1-9]\d{9}$/.test(mobile);
        }
        return /^\d{11}$/.test(mobile);
    })();
    const isIdentifierValid = loginMode === 'mobile' ? isMobileValid : isUsernameValid;
    const showIdentifierError =
        loginMode === 'mobile'
            ? loginMobile.length > 0 && !isMobileValid
            : loginUsername.length > 0 && !isUsernameValid;
    const identifierErrorMessage =
        loginMode === 'mobile'
            ? (loginConfigInvalidPhoneNumberErrorMessage?.trim()
                ? loginConfigInvalidPhoneNumberErrorMessage
                : t('loginMobileInvalid'))
            : (loginConfigInvalidUsernameErrorMessage?.trim()
                ? loginConfigInvalidUsernameErrorMessage
                : t('loginUsernameInvalid'));
    const errorColor = loginConfigErrorColor?.trim()
        ? loginConfigErrorColor.slice(0, 7)
        : '#EF4444';

    const canSubmitLogin = Boolean(loginIdentifier && loginPassword && isIdentifierValid);

    // LoginModal remounts each time it is shown — restore remembered values on display
    useEffect(() => {
        const rememberedMode = window.localStorage?.getItem(rememberedLoginModeKey) as LoginMode | null;
        const rememberedUsername = window.localStorage?.getItem(rememberedUsernameKey);
        const rememberedMobile = window.localStorage?.getItem(rememberedMobileKey);

        if (rememberedMode === 'mobile' && loginConfigEnablePhoneLogin) {
            setLoginMode('mobile');
        } else if (rememberedMode === 'username' || !loginConfigEnablePhoneLogin) {
            setLoginMode('username');
        }
        if (rememberedUsername) {
            setLoginUsername(rememberedUsername);
            setRememberMe(true);
        }
        if (rememberedMobile && loginConfigEnablePhoneLogin) {
            setLoginMobile(rememberedMobile);
            setRememberMe(true);
        }
    }, [rememberedUsernameKey, rememberedMobileKey, rememberedLoginModeKey, loginConfigEnablePhoneLogin]);

    useEffect(() => {
        if (!loginConfigEnablePhoneLogin && loginMode === 'mobile') {
            setLoginMode('username');
        }
    }, [loginConfigEnablePhoneLogin, loginMode]);

    const failureMessageProcess = (failDescription: string) => {
        // const content = {
        //     owner: 'bot',
        //     name: botName,
        //     type: 'text',
        //     message: {
        //         content: failDescription,
        //     },
        //     displayTime: format(new Date(), 'hh:mmaa'),
        //     createdBy: new Date().getTime(),
        // } as ChatInterface;
        // cacheService.pushCache(
        //     sessionId + '_chats',
        //     JSON.stringify(content)
        // );
        // dispatch(addChat(content));
        // socket.sendUploadChatEvent({
        //     chatId: chatId,
        //     speaker: 'Bot',
        //     speakerName: botName,
        //     datetime: new Date().toJSON(),
        //     message: failDescription
        // });
        let data = {
            message: "Done (login not successful)",
            type: 'message',
            username: username,
            chatId: chatId,
            customer_lang_culture:
                botLanguage.toLowerCase() === 'chinese'
                    ? 'zh-cn'
                    : botLanguage.toLowerCase() === 'thai'
                    ? 'th-th'
                    : botLanguage.toLowerCase() === 'vietnamese'
                    ? 'vi-vn'
                    : botLanguage.toLowerCase() === 'vietnam'
                    ? 'vi-vn'
                    : botLanguage.toLowerCase() === 'english'
                    ? 'en'
                    : botLanguage.toLowerCase(),
            operating_system: getOS(navigator),
            screen_resolution:
                window.screen.width + '*' + window.screen.height,

            platform: 'mario',
            group_name: '',
            processor: processor,
            brandInfo : {
                brand: brandCode,
                market: marketCode
            },
        };
        socket.sendMessageEvent(data);
        // dispatch(editStatus({ name: 'isBotTyping', value: false }))
    };
    
    const botConfig = () => {
        if (dbProfile === 'mario.production') {
            return ProductionConfig.find(config => config.botId === parseInt(botId) && config.accountId === parseInt(acctId));
        } else {
            return StagingConfig.find(config => config.botId === parseInt(botId) && config.accountId === parseInt(acctId));
        }
    }

    // const dynamicScript = (callback: any) => {
    //     const existingScript = document.getElementById('dynamicjs');
    //     if (!existingScript) {
    //         const script = document.createElement('script');
    //         script.src = HashscriptUrl;
    //         script.id = 'dynamicjs';
    //         document.body.appendChild(script);
    //         script.onload = () => {
    //             console.log('script loaded!!!');
    //             callback()
    //         }
    //     }
    // };
    // useEffect(() => {
    //     dynamicScript(() => {
    //         window.encryptValue(loginPassword)
    //     });
    // }, []);

    const ProcessLogin = () => {
        if (pendingLoginHyperlinkMessageId) {
            const nextUsedLoginHyperlinkMessageIds =
                appendUsedLoginHyperlinkMessageId(
                    usedLoginHyperlinkMessageIds,
                    pendingLoginHyperlinkMessageId
                );
            dispatch(
                editSession({
                    name: USED_LOGIN_HYPERLINK_MESSAGE_IDS_KEY,
                    value: nextUsedLoginHyperlinkMessageIds,
                })
            );
            setCacheByField(
                sessionId,
                USED_LOGIN_HYPERLINK_MESSAGE_IDS_KEY,
                JSON.stringify(nextUsedLoginHyperlinkMessageIds)
            );
            dispatch(
                editStatus({
                    name: 'pendingLoginHyperlinkMessageId',
                    value: '',
                })
            );
        }

        const trimmedIdentifier = loginIdentifier.trim();
        const mobileValue = `${mobileDialCode}-${trimmedIdentifier}`;

        if (rememberMe && trimmedIdentifier) {
            window.localStorage?.setItem(rememberedLoginModeKey, loginMode);
            if (loginMode === 'mobile') {
                window.localStorage?.setItem(rememberedMobileKey, trimmedIdentifier);
            } else {
                window.localStorage?.setItem(rememberedUsernameKey, trimmedIdentifier);
            }
        } else {
            window.localStorage?.removeItem(rememberedLoginModeKey);
            window.localStorage?.removeItem(rememberedUsernameKey);
            window.localStorage?.removeItem(rememberedMobileKey);
        }
        openLoadingModal();
        closeLoginModal();
        const authPublicKeyUrl = `${pluginPath}/api/getAuthPublicKey`;
        axios.post(authPublicKeyUrl).then(function(response){
            const authPublicKey = response.data.public_key
            const LoginParams = loginMode === 'mobile'
                ? {
                    mobile: mobileValue,
                    secured_password_hash: loginPassword,
                    acct_id: acctId.toString(),
                    bot_id: botId.toString(),
                    session_id: chatId,
                    is_vip: vip[0]
                }
                : {
                    user_name: trimmedIdentifier,
                    secured_password_hash: loginPassword,
                    acct_id: acctId.toString(),
                    bot_id: botId.toString(),
                    session_id: chatId,
                    is_vip: vip[0]
                }

            console.log("LoginParams: ", LoginParams)
            
            const loginParamsEncryted = getEncrypt64(LoginParams, authPublicKey)
            // const submitLoginUrl = `${pluginPath}/api/submitPasswordHash`;
                            
            // const submitLoginJson = {
            //     valueEncryted: loginParamsEncryted,
            //     cache_session_id: botId.toString() + sessionId,
            //     cache_url: apiPath,
            //     brand_id : brandId,
            //     checkVipDetails: parsedCustomValues['check_vip_details'] ? parsedCustomValues['check_vip_details'] : null 
            // };
            socket.sendLoginEvent({
                valueEncryted: loginParamsEncryted,
                cacheId: botId.toString() + sessionId,
                cacheUrl: apiPath,
                brand_id : brandId,
                checkVipDetails: parsedCustomValues['check_vip_details'] ? parsedCustomValues['check_vip_details'] : null ,
                chatId: chatId,
                brandCode: brandCode,
                marketCode: marketCode,
                isVipBot: isVipBot,
                ...(loginMode === 'mobile' ? { loginType: 3 } : {}),
            })
            // console.log('submitLoginJson:', submitLoginJson, 'url:', `${pluginPath}/api/submitPasswordHash`);
            // axios
            //     .post(submitLoginUrl, submitLoginJson)
            //     .then(function (response) {
            //         const loginResponse = response.data;
            //         console.log("RESPONSE IS HERE",response);
            //         closeLoadingModal();
            //         if (loginResponse.login_status) {
            //             let cacheInfo = {
            //                 sessionId: botId.toString() + sessionId,
            //                 cacheUrl: apiPath,
            //                 botName: botName
            //             }
            //             if (processor == 2 || processor == 3){
            //                 let data = {
            //                     message: "Done (login successful)",
            //                     type: 'message',
            //                     username: username,
            //                     chatId: chatId,
            //                     customer_lang_culture:
            //                         botLanguage.toLowerCase() === 'chinese'
            //                             ? 'zh-cn'
            //                             : botLanguage.toLowerCase() === 'thai'
            //                             ? 'th-th'
            //                             : botLanguage.toLowerCase() === 'vietnamese'
            //                             ? 'vi-vn'
            //                             : botLanguage.toLowerCase() === 'vietnam'
            //                             ? 'vi-vn'
            //                             : botLanguage.toLowerCase() === 'english'
            //                             ? 'en'
            //                             : botLanguage.toLowerCase(),
            //                     operating_system: getOS(navigator),
            //                     screen_resolution:
            //                         window.screen.width + '*' + window.screen.height,
            
            //                     platform: 'mario',
            //                     group_name: '',
            //                     processor: processor,
            //                     cacheInfo: cacheInfo,
            //                     brandInfo : {
            //                         brand: brandCode,
            //                         market: marketCode
            //                     },
            //                 };
            //                 socket.sendMessageEvent(data);
            //                 dispatch(editSession({ name: 'username', value: loginUsername }));
            //                 setCacheByField(sessionId, 'username', loginUsername);
            //                 dispatch(editSession({ name: 'waitingAnswerReply', value: true }));
            //                 cacheService.setCacheByField(sessionId, 'waitingAnswerReply', true)
            //                 dispatch(editSession({ name: 'isAuthenticated', value: true }));
            //                 setCacheByField(sessionId, 'isAuthenticated', true)
            //                 openSuccessLoginModal();
            //                 socket.sendCheckVipEvent({
            //                     chatId: chatId,
            //                     username: loginUsername,
            //                     usernameFromUrl: false, //check
            //                     recheckVip: true,
            //                     checkVipDetails: parsedCustomValues['check_vip_details'] ? parsedCustomValues['check_vip_details'] : null,
            //                     brandCode: brandCode
            //                 })
            //             console.log('SUCCESS LOGIN');
            //             };
            //             if (processor == 0){
            //                 let cacheInfo = {
            //                     sessionId: botId.toString() + sessionId,
            //                     cacheUrl: apiPath,
            //                     botName: botName
            //                 }
            //                 let data = {
            //                     username: username,
            //                     chatId: chatId,
            //                     type: 'goToIntent',
            //                     intentId: intentId,
            //                     triggerTime: new Date().toJSON(),
            //                     platform: 'mario',
            //                     group_name: '',
            //                     customer_lang_culture:
            //                         botLanguage.toLowerCase() ===
            //                         'chinese'
            //                             ? 'zh-cn'
            //                             : botLanguage.toLowerCase() ===
            //                             'thai'
            //                             ? 'th-th'
            //                             : botLanguage.toLowerCase() ===
            //                             'vietnamese'
            //                             ? 'vi-vn'
            //                             : botLanguage.toLowerCase() ===
            //                             'vietnam'
            //                             ? 'vi-vn'
            //                             : botLanguage.toLowerCase() ===
            //                             'english'
            //                             ? 'en'
            //                             : botLanguage.toLowerCase(),
            //                     operating_system: getOS(navigator),
            //                     screen_resolution:
            //                         window.screen.width +
            //                         '*' +
            //                         window.screen.height,
            //                     processor: processor,
            //                     cacheInfo: cacheInfo,
            //                     brandInfo : {
            //                         brand: brandCode,
            //                         market: marketCode
            //                     }
            //                 } as SendIntentEventInterface;
            //                 socket.sendIntentEvent(data);
            //             };    
            //         } else {
            //             if (processor == 2 || processor == 3){
            //                 if (loginResponse.description == "[ERROR_ON_LOGIN]"){
            //                     loginResponse.description = t('error_login')
            //                 }
            //                 failureMessageProcess(loginResponse.description)
            //             }
            //             openFailLoginModal();
            //             console.log('LOGIN FAILED');
            //         }
            //     })
            //     .catch(function (error) {
            //         console.error('Error during login:', error);
            //         const failDescription = t('error_login')
            //         failureMessageProcess(failDescription)
            //         openFailLoginModal();
            //     })
            //     .finally(function () {
            //         closeLoadingModal();
            //     });
        })
        .catch(function (error) {
            console.error('Error while getting public key:', error);
            const failDescription = t('error_login')
            failureMessageProcess(failDescription)
            closeLoadingModal();
            openFailLoginModal();
        })        
    };

    return (
        <>
            {isChatAlive && (
                <div
                    style={{ 
                        height: props.height, 
                        fontFamily: "'Times New Roman', Times, serif", 
                    }}
                    className={classNames(
                        'fixed w-screen h-screen bg-zinc-800/90 flex justify-center items-center z-20'
                    )}
                >
                    <div
                        className="relative w-90pc xs:w-64 small:w-96 medium:w-128 shadow-lg rounded-xl overflow-hidden pt-4 pb-3"
                        style={{ backgroundColor: modalBackgroundColor }}
                    >
                        <style>
                            {`
                                .login-modal-input:-webkit-autofill,
                                .login-modal-input:-webkit-autofill:hover,
                                .login-modal-input:-webkit-autofill:focus,
                                .login-modal-input:-webkit-autofill:active {
                                    -webkit-box-shadow: 0 0 0px 1000px ${inputBackgroundColor} inset !important;
                                    box-shadow: 0 0 0px 1000px ${inputBackgroundColor} inset !important;
                                    -webkit-text-fill-color: ${inputTextColor} !important;
                                    caret-color: ${inputTextColor};
                                    transition: background-color 99999s ease-in-out 0s;
                                }
                            `}
                        </style>
                        <div className="relative w-90pc mx-auto flex justify-end">
                            <button
                                onClick={closeLoginModal}
                                type="button"
                                className="z-10 leading-none"
                                style={{ color: fontPrimaryColor }}
                                aria-label="Close"
                            >
                                <FontAwesomeIcon icon={faXmark} className="text-lg" />
                            </button>
                        </div>
                        <div className="flex justify-center items-center px-4 pt-5 pb-5 rounded-t">
                            <h3
                                className="text-2xl font-bold"
                                style={{ color: fontPrimaryColor }}
                            >
                                {loginConfigTitle?.trim() ? loginConfigTitle : t('loginTitle')}
                            </h3>
                        </div>
                        {loginConfigEnablePhoneLogin && (
                            isButtonTabType ? (
                                <div
                                    className="w-90pc mx-auto mb-3 p-1 rounded-lg flex text-sm small:text-base medium:text-lg"
                                    style={{ backgroundColor: buttonTabTrackColor }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setLoginMode('username')}
                                        className={classNames(
                                            'flex-1 py-2 rounded-md transition-colors',
                                            loginMode === 'username'
                                                ? 'font-bold shadow-sm'
                                                : 'font-medium'
                                        )}
                                        style={{
                                            color: loginMode === 'username' ? fontPrimaryColor : inactiveTabColor,
                                            backgroundColor: loginMode === 'username' ? selectedButtonTabColor : 'transparent',
                                        }}
                                    >
                                        {t('loginUsername')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setLoginMode('mobile')}
                                        className={classNames(
                                            'flex-1 py-2 rounded-md transition-colors',
                                            loginMode === 'mobile'
                                                ? 'font-bold shadow-sm'
                                                : 'font-medium'
                                        )}
                                        style={{
                                            color: loginMode === 'mobile' ? fontPrimaryColor : inactiveTabColor,
                                            backgroundColor: loginMode === 'mobile' ? selectedButtonTabColor : 'transparent',
                                        }}
                                    >
                                        {t('loginMobile')}
                                    </button>
                                </div>
                            ) : (
                                <div className="w-90pc mx-auto flex border-b border-gray-200 mb-3 text-sm small:text-base medium:text-lg">
                                    <button
                                        type="button"
                                        onClick={() => setLoginMode('username')}
                                        className={classNames(
                                            'flex-1 py-2 transition-colors',
                                            loginMode === 'username' ? 'border-b-2 font-bold' : 'font-medium'
                                        )}
                                        style={
                                            loginMode === 'username'
                                                ? { color: activeColor, borderColor: activeColor }
                                                : { color: inactiveTabColor }
                                        }
                                    >
                                        {t('loginUsername')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setLoginMode('mobile')}
                                        className={classNames(
                                            'flex-1 py-2 transition-colors',
                                            loginMode === 'mobile' ? 'border-b-2 font-bold' : 'font-medium'
                                        )}
                                        style={
                                            loginMode === 'mobile'
                                                ? { color: activeColor, borderColor: activeColor }
                                                : { color: inactiveTabColor }
                                        }
                                    >
                                        {t('loginMobile')}
                                    </button>
                                </div>
                            )
                        )}
                        <div className="content text-center text-sm">
                            {loginMode === 'username' ? (
                                <div className="relative mb-2 mt-2 w-90pc mx-auto text-left">
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-300">
                                            <FontAwesomeIcon icon={faUser} />
                                        </span>
                                        <input
                                            onChange={handleUsernameChange}
                                            className={classNames(
                                                "login-modal-input pl-10 pr-4 py-3 w-full rounded-lg focus:outline-none shadow-lg border",
                                                !showIdentifierError && "border-gray-300",
                                                !isButtonTabType && "placeholder:text-[#6B7280]",
                                                isButtonTabType && "placeholder:text-[#9CA3AF]"
                                            )}
                                            style={{
                                                ...(showIdentifierError ? { borderColor: errorColor } : {}),
                                                backgroundColor: inputBackgroundColor,
                                                color: inputTextColor,
                                            }}
                                            placeholder={usernamePlaceholder}
                                            value={loginUsername}
                                            type="text"
                                            maxLength={16}
                                        />
                                    </div>
                                    {showIdentifierError && (
                                        <p
                                            className="mt-1 text-xs"
                                            style={{ color: errorColor }}
                                        >
                                            {identifierErrorMessage}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="relative mb-2 mt-2 w-90pc mx-auto text-left">
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-300">
                                            <FontAwesomeIcon icon={faMobileScreen} />
                                        </span>
                                        <span className="absolute left-10 top-1/2 transform -translate-y-1/2 text-gray-500">
                                            +{mobileDialCode}
                                        </span>
                                        <input
                                            onChange={handleMobileChange}
                                            className={classNames(
                                                "login-modal-input pl-20 pr-4 py-3 w-full rounded-lg focus:outline-none shadow-lg border",
                                                !showIdentifierError && "border-gray-300",
                                                !isButtonTabType && "placeholder:text-[#6B7280]",
                                                isButtonTabType && "placeholder:text-[#9CA3AF]"
                                            )}
                                            style={{
                                                ...(showIdentifierError ? { borderColor: errorColor } : {}),
                                                backgroundColor: inputBackgroundColor,
                                                color: inputTextColor,
                                            }}
                                            placeholder={mobilePlaceholder}
                                            value={loginMobile}
                                            type="tel"
                                            inputMode="numeric"
                                            maxLength={mobileDialCode === '86' ? 11 : mobileDialCode === '66' ? 9 : 10}
                                        />
                                    </div>
                                    {showIdentifierError && (
                                        <p
                                            className="mt-1 text-xs"
                                            style={{ color: errorColor }}
                                        >
                                            {identifierErrorMessage}
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="relative mb-4 mt-2 w-90pc mx-auto">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-300">
                                    <FontAwesomeIcon icon={faUnlockKeyhole} className="text-lg" />
                                </span>
                                <input
                                    onChange={handlePasswordChange}
                                    className={classNames(
                                        "login-modal-input pl-10 pr-10 py-3 w-full rounded-lg focus:outline-none shadow-lg border border-gray-300",
                                        "[&::-ms-reveal]:hidden [&::-ms-clear]:hidden",
                                        !isButtonTabType && "placeholder:text-[#6B7280]",
                                        isButtonTabType && "placeholder:text-[#9CA3AF]"
                                    )}                            
                                    style={{
                                        backgroundColor: inputBackgroundColor,
                                        color: inputTextColor,
                                    }}
                                    placeholder={passwordPlaceholder}
                                    value={loginPassword}
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                                </button>
                            </div>
                        </div>
                        <div className="relative mb-1 mt-3 w-90pc mx-auto flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={handleRememberMeChange}
                                    className="sr-only"
                                />
                                <span
                                    className="relative inline-flex items-center justify-center w-4 h-4 rounded-sm border flex-shrink-0"
                                    style={{
                                        borderColor: rememberMe ? activeColor : '#9CA3AF',
                                        backgroundColor: 'transparent',
                                    }}
                                >
                                    {rememberMe && (
                                        <span
                                            className="absolute"
                                            style={{
                                                width: '5px',
                                                height: '9px',
                                                borderRight: `2px solid ${activeColor}`,
                                                borderBottom: `2px solid ${activeColor}`,
                                                transform: 'rotate(45deg) translate(-1px, -1px)',
                                            }}
                                        />
                                    )}
                                </span>
                                <span
                                    style={{
                                        fontSize: '85%',
                                        color: loginConfigFontPrimaryColor?.trim()
                                            ? loginConfigFontPrimaryColor.slice(0, 7)
                                            : '#4B5563',
                                    }}
                                >
                                    {rememberMeText}
                                </span>
                            </label>
                            {showForgotPassword && (
                                <a 
                                    href={showForgotPassword}
                                    className="hover:underline"
                                    style={{
                                        fontSize: '85%',
                                        color: activeColor,
                                    }}
                                >
                                    {forgetPasswordText}
                                </a>
                            )}
                        </div>
                        <div>
                            <button
                                style={{
                                    backgroundColor: canSubmitLogin
                                        ? buttonColor
                                        : inactiveSubmitButtonColor,
                                    color: canSubmitLogin
                                        ? activeSubmitTextColor
                                        : inactiveSubmitTextColor,
                                }}
                                onClick={ProcessLogin}
                                disabled={!canSubmitLogin}
                                type="button"
                                className={classNames(
                                    'flex justify-center items-center w-90pc px-4 py-3 rounded-lg mx-auto mb-2 mt-5 font-bold shadow-lg',
                                    {
                                        'cursor-not-allowed' : !canSubmitLogin,
                                    }
                                )}
                            >
                                {loginButtonText}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

LoginModal.propTypes = {};

export default LoginModal;
